import React, { useState, useRef, useEffect } from "react";
import { shape, string, number, func, bool, arrayOf } from "prop-types";

import { connect } from "react-redux";

import moment from "moment";

import history from "../history";

import {
  partsToDate,
  getDateParts,
  isDateValid,
  mergeShallow,
  pick,
  getFieldValue,
  isEmpty,
} from "../helpers";

import { Validate } from "../common";

import { birthKeys } from "../components/BasicInfo";

import BasicInfoEdit from "../components/BasicInfo/BasicInfoEdit/index";

import {
  updateUserInProfileSilent,
  getAddressList as getAddressListAction,
} from "../redux/actions";

import { useDebounce } from "../helpers/hooks";

import getNearestBranch from "../helpers/nearestBranch";

import {
  excludedUmbracoBranches,
  nonChangableTeamCodes,
} from "../helpers/helpers";

import { branchPropTypes } from "../components/SelectBranch";

const constraints = {
  mobileTelephone: {
    isValidTelephone: true,
  },
  homeTelephone: {
    isValidTelephone: true,
  },
  birthYear: {
    isDateValid: {
      excludeDay: true,
      keys: birthKeys,
    },
    isDateBefore: {
      keys: birthKeys,
      date: moment().subtract({ years: 16 }),
      roundUpDay: true,
    },
  },
};

const useBasicInfoState = (appData) => {
  const birthdate = getDateParts(appData.dateOfBirth, birthKeys);

  const initialState = {
    title: getFieldValue(appData, "title"),
    firstName: getFieldValue(appData, "firstName"),
    lastName: getFieldValue(appData, "lastName"),
    mobileTelephone: getFieldValue(appData, "mobileTelephone"),
    homeTelephone: getFieldValue(appData, "homeTelephone"),
    email: getFieldValue(appData, "emailAddress"),
    responsibleTeam: getFieldValue(appData, "responsibleTeam"),
    postcode: getFieldValue(appData, ["address", "postcode"]),
    addressLine1: getFieldValue(appData, ["address", "addressLine1"]),
    addressLine2: getFieldValue(appData, ["address", "addressLine2"]),
    addressLine3: getFieldValue(appData, ["address", "addressLine3"]),
    county: getFieldValue(appData, ["address", "county"]),
    town: getFieldValue(appData, ["address", "town"]),
    country: "UK",
    addressRef: getFieldValue(appData, ["address", "addressRef"]),
    ...birthdate,
  };

  const [state, setState] = useState(initialState);

  const onChange = (key, val) => {
    setState(mergeShallow(state, { [key]: val }));
  };

  return [state, onChange];
};

const useOnSubmit = ({
  appData,
  branchCode,
  birthDay,
  birthMonth,
  birthYear,
  updateProfile,
  matchedPostcodePosition,
  ...state
}) => {
  return () => {
    const dateOfBirth = partsToDate(birthDay, birthMonth, birthYear);
    const combinedState = { ...appData, ...state };
    const userData = {
      // Order of `appData` and `state` is important as any changes in `state` should
      ...pick(combinedState, [
        "candidateRef",
        "emailAddress",
        "title",
        "firstName",
        "lastName",
        "homeTelephone",
        "mobileTelephone",
      ]),
      responsibleTeam: branchCode,
      address: {
        ...matchedPostcodePosition,
        ...pick(combinedState, [
          "addressRef",
          "addressLine1",
          "addressLine2",
          "addressLine3",
          "town",
          "county",
          "country",
          "postcode",
        ]),
      },
      dateOfBirth: isDateValid(dateOfBirth) ? moment(dateOfBirth).format() : "",
      agreesToMailshot: combinedState.agreesToMailshot,
    };

    Promise.all([
      updateProfile(appData.candidateRef, userData),
      history.push("/profile"),
    ]);
  };
};

const useBranchState = (initialBranch) => useState(initialBranch);

const BasicInfoUpdate = ({
  appData,
  description,
  loading,
  updateProfile,
  branches,
  matchedPostcodePosition,
  getAddressList,
}) => {
  if (!appData) return null;

  const [state, onChange] = useBasicInfoState(appData);

  const [branchCode, setBranchCode] = useBranchState(appData.responsibleTeam);

  // This is used to track when the branchCode should be updated
  const hasPostcodeChanged = useRef(false);

  useDebounce(
    state.postcode,
    (postcode) => {
      if (hasPostcodeChanged.current) {
        getAddressList(postcode);
      }
    },
    1500
  );

  useEffect(() => {
    if (hasPostcodeChanged.current && matchedPostcodePosition.lat) {
      hasPostcodeChanged.current = false;
      const branch = getNearestBranch(
        matchedPostcodePosition,
        branches.filter((b) => !excludedUmbracoBranches.includes(b.code))
      );
      const responsibleTeam = branch !== undefined ? branch.code : "";
      setBranchCode(responsibleTeam);
    }
  }, [matchedPostcodePosition.lat]);

  // Create hooks to track which fields have been changed
  const [itemsChanged, setItemChanged] = useState({
    homeTelephone: false,
    mobileTelephone: false,
  });

  const [hasDobChanged, setDobChanged] = useState(false);

  // Create onSubmit handler
  const onSubmit = useOnSubmit({
    ...state,
    appData,
    branchCode,
    updateProfile,
    matchedPostcodePosition,
  });

  // Create handlers and flags for dob and telephone changes
  const additionalProps = {
    onTelephoneChange: (key, val) => {
      setItemChanged({
        ...itemsChanged,
        [key]: true,
      });
      onChange(key, val);
    },
    onDobChange: (key, val) => {
      setDobChanged(true);
      onChange(key, val);
    },
    hasDobChanged,
  };

  const onThisChanged = (key, val) => {
    if (key === "postcode" && !nonChangableTeamCodes.includes(branchCode)) {
      hasPostcodeChanged.current = true;
    }
    if (key === "responsibleTeam") {
      setBranchCode(val);
    } else {
      onChange(key, val);
    }
  };

  return (
    <Validate constraints={constraints} userInput={state}>
      {({ validation }) => (
        <BasicInfoEdit
          validation={validation}
          onSubmit={onSubmit}
          loading={loading}
          appData={appData}
          description={description}
          itemsChanged={itemsChanged}
          handleChange={onThisChanged}
          branchCode={branchCode}
          branches={branches}
          {...additionalProps}
          {...state}
        />
      )}
    </Validate>
  );
};

BasicInfoUpdate.propTypes = {
  appData: shape({
    candidateRef: number.isRequired,
    title: string.isRequired,
    firstName: string.isRequired,
    lastName: string.isRequired,
    dateOfBirth: string.isRequired,
    homeTelephone: string.isRequired,
    mobileTelephone: string.isRequired,
  }),
  matchedPostcodePosition: shape({
    lat: number.isRequired,
    long: number.isRequired,
  }),
  branches: arrayOf(shape(branchPropTypes)),
  description: string.isRequired,
  loading: bool.isRequired,
  getAddressList: func.isRequired,
  updateProfile: func.isRequired,
};

const mapStateToProps = (state) => ({
  user: state.core.user,
  appData: state.core.app,
  matchedPostcodePosition: {
    lat: state.core.addressList.latitude,
    long: state.core.addressList.longitude,
  },
  description: state.core.umbracoContent.updateSub,
  branches: state.core.umbracoContent.branches,
  loading: state.core.loading,
});

const mapDispatchToProps = {
  updateProfile: updateUserInProfileSilent,
  getAddressList: getAddressListAction,
};

const withRedux = connect(mapStateToProps, mapDispatchToProps);

export default withRedux(BasicInfoUpdate);
